import { useEffect } from 'react';
import { QueryClient, RefetchQueryFilters, useQueryClient } from 'react-query';
import { useAppForeground } from 'src/hooks/useAppForeground';
import { queryKeys } from 'src/queries/keys';
import { isNative } from 'src/utils/platform';

const REFETCH_IF_STALE: RefetchQueryFilters[] = [
  { queryKey: queryKeys.user(), exact: true },
  { queryKey: queryKeys.users(), exact: true },
  { queryKey: queryKeys.wallet(), exact: true },
  { queryKey: queryKeys.streak(), exact: true },
  { queryKey: queryKeys.starRoad(), exact: true },
  {
    queryKey: queryKeys.exams(),
    predicate: ({ queryKey }) => typeof queryKey[1] === 'object',
  },
];

const INVALIDATE = [
  queryKeys.notifications(),
  queryKeys.unreadNotifications(),
  queryKeys.milestones(),
];

let lastRefreshAt = 0;
const DEDUPE_MS = 1000;

const refreshOnForeground = (queryClient: QueryClient) => {
  const now = Date.now();
  if (now - lastRefreshAt < DEDUPE_MS) return;
  lastRefreshAt = now;
  REFETCH_IF_STALE.forEach(filters => {
    void queryClient.refetchQueries({ ...filters, active: true, stale: true });
  });
  INVALIDATE.forEach(queryKey => {
    void queryClient.invalidateQueries({ queryKey, exact: true });
  });
};

export const useRefreshOnForeground = ({ isEnabled }: { isEnabled: boolean }) => {
  const queryClient = useQueryClient();
  useAppForeground(() => refreshOnForeground(queryClient), {
    isEnabled,
    isVisibilityIncluded: !isNative(),
  });
  useEffect(() => {
    if (!isEnabled || isNative()) return;
    const onFocus = () => refreshOnForeground(queryClient);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [isEnabled, queryClient]);
};
